import { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    IconButton,
    Grid,
    Divider,
    Snackbar,
    Alert,
    CircularProgress,
    Container,
    MenuItem,
    Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { getBackendUrl } from '../util/envFn';
import api from '../api';

// Define the schema for a single crawler setting
const crawlerSettingSchema = z.object({
    _id: z.string().optional(),
    searchTerm: z.string(),
    provider: z.string().min(1, 'Provider is required'),
    pmEmail: z.string().email('Invalid email format')
});

// Define the schema for the entire form
const formSchema = z.object({
    settings: z.array(crawlerSettingSchema)
        .nonempty('At least one setting is required')
        .refine(
            settings => {
                const providers = settings.map(s => s.provider);
                return new Set(providers).size === providers.length;
            },
            { message: 'Providers must be unique', path: ['settings'] }
        )
});

type FormData = z.infer<typeof formSchema>;

const providers = [
    "JobsDB",
]

const ServerSettings = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Initialize the form
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            settings: []
        }
    });

    // Use field array to handle the dynamic list of settings
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'settings'
    });

    // Fetch current settings from the server
    useEffect(() => {
        const fetchSettings = async () => {
            setIsLoading(true);
            try {
                const response = await api.get(getBackendUrl('/crawlerSettings'));
                reset({ settings: response.data });
            } catch (error) {
                console.error('Error fetching settings:', error);
                setFetchError('Failed to load settings. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, [reset]);

    // Submit the form data
    const onSubmit: SubmitHandler<FormData> = async (data) => {
        try {
            const response = await api.put(getBackendUrl('/crawlerSettings'), data);

            if (response.status !== 200) {
                const errorData = await response.data;
                throw new Error(errorData.error || 'Failed to update settings');
            }

            const updatedSettings = await response.data;
            reset({ settings: updatedSettings });
            setSuccessMessage('Settings updated successfully!');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
            console.error('Error updating settings:', error);
            if (error instanceof Error) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage('An unknown error occurred');
            }

            // Clear error message after 5 seconds
            setTimeout(() => setErrorMessage(null), 5000);
        }
    };

    // Add a new empty setting
    const addNewSetting = () => {
        append({ searchTerm: '', provider: 'JobsDB', pmEmail: '' });
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
                <CircularProgress />
            </Box>
        );
    }

    if (fetchError) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
                <Alert severity="error">{fetchError}</Alert>
            </Box>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Crawler Settings
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Manage the crawler settings for job searching
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    {errors.settings?.message && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errors.settings.message}
                        </Alert>
                    )}

                    {fields.map((field, index) => (
                        <Paper key={field.id} elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <Controller
                                        name={`settings.${index}.provider`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                select
                                                size="small"
                                                label="Provider"
                                                variant="outlined"
                                                fullWidth
                                                error={!!errors.settings?.[index]?.provider}
                                                helperText={errors.settings?.[index]?.provider?.message || ''}
                                            >
                                                {/* <MenuItem value="">Select a provider</MenuItem> */}
                                                {providers.map(option => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Controller
                                        name={`settings.${index}.searchTerm`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                size="small"
                                                label="Search Term (Separate by comma)"
                                                variant="outlined"
                                                fullWidth
                                                error={!!errors.settings?.[index]?.searchTerm}
                                                helperText={errors.settings?.[index]?.searchTerm?.message || ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Controller
                                        name={`settings.${index}.pmEmail`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                size="small"
                                                label="PM Email"
                                                variant="outlined"
                                                fullWidth
                                                error={!!errors.settings?.[index]?.pmEmail}
                                                helperText={errors.settings?.[index]?.pmEmail?.message || ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={1}>
                                    <IconButton
                                        aria-label="Delete setting"
                                        onClick={() => remove(index)}
                                        color="error"
                                        disabled={fields.length === 1}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}

                    <Stack direction="row" gap={2} justifyContent="space-between" mt={3}>
                        {
                            fields.length < providers.length ? (
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addNewSetting}
                                    variant="outlined"
                                >
                                    Add Setting
                                </Button>
                            ) : <Box />
                        }
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Saving...' : 'Save Settings'}
                        </Button>
                    </Stack>
                </Box>

                {/* Snackbars for success/error messages */}
                <Snackbar
                    open={!!successMessage}
                    autoHideDuration={3000}
                    onClose={() => setSuccessMessage(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSuccessMessage(null)} severity="success">
                        {successMessage}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!errorMessage}
                    autoHideDuration={5000}
                    onClose={() => setErrorMessage(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setErrorMessage(null)} severity="error">
                        {errorMessage}
                    </Alert>
                </Snackbar>
            </Paper>
        </Container>
    );
};

export default ServerSettings;

